import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Plus, Trash2, ShieldCheck, Users as UsersIcon, Loader2, Sparkles, Banknote, Headset, FileSpreadsheet, UserCheck, CheckCircle2 } from "lucide-react";

const TEMPLATE_ICONS = { ShieldCheck, Banknote, Headset, FileSpreadsheet, UserCheck };

export default function AdminRoles() {
  const [custom, setCustom] = useState([]);
  const [builtin, setBuiltin] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deletingSlug, setDeletingSlug] = useState(null);
  const [installingId, setInstallingId] = useState(null);

  const [form, setForm] = useState({ name: "", side: "admin", parent: "admin", description: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: rolesData }, { data: tplData }] = await Promise.all([
        api.get("/admin/custom-roles"),
        api.get("/admin/role-templates"),
      ]);
      setCustom(rolesData.custom || []);
      setBuiltin(rolesData.builtin || []);
      setTemplates(tplData.templates || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load roles");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onCreate = async () => {
    if (form.name.trim().length < 2) { toast.error("Role name too short"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        side: form.side,
        parent: form.parent === "none" ? null : form.parent,
        description: form.description.trim() || null,
      };
      await api.post("/admin/custom-roles", payload);
      toast.success(`Role "${form.name}" created`);
      setOpen(false);
      setForm({ name: "", side: "admin", parent: "admin", description: "" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create role");
    } finally { setBusy(false); }
  };

  const onDelete = async (slug, name, userCount) => {
    if (userCount > 0) { toast.error(`${userCount} user(s) still assigned — re-assign first`); return; }
    if (!window.confirm(`Delete role "${name}"? This is permanent.`)) return;
    setDeletingSlug(slug);
    try {
      await api.delete(`/admin/custom-roles/${slug}`);
      toast.success(`Role "${name}" deleted`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    } finally { setDeletingSlug(null); }
  };

  const onInstallTemplate = async (tpl) => {
    setInstallingId(tpl.id);
    try {
      await api.post("/admin/role-templates/install", { template_id: tpl.id });
      toast.success(`"${tpl.name}" installed · 27 features pre-configured`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Install failed");
    } finally { setInstallingId(null); }
  };

  const parentChoices = useMemo(() => [
    { value: "admin", label: "Admin (full access — trim down)" },
    { value: "partner", label: "Partner (retailer baseline)" },
    { value: "none", label: "Empty (build from scratch)" },
    ...custom.map(c => ({ value: c.slug, label: `${c.name} (custom)` })),
  ], [custom]);

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Functional Control · Roles</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 flex items-center gap-2">
            <KeyRound className="h-7 w-7 text-primary" strokeWidth={1.5} /> Custom roles
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            Define new user types (e.g. Finance Manager, Support L1, Compliance Officer). Each role gets its own column in the Feature matrix — toggle exactly which features it can use.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="open-create-role">
          <Plus className="h-4 w-4 mr-1" /> New role
        </Button>
      </div>

      {/* Roles Marketplace — pre-built templates */}
      <div className="mt-8" data-testid="role-templates-section">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-secondary" />
          <h2 className="font-display text-xl font-semibold">Roles Marketplace</h2>
          <span className="text-xs text-muted-foreground">· One-click install with curated feature scopes</span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((tpl) => {
            const Icon = TEMPLATE_ICONS[tpl.icon] || Sparkles;
            return (
              <div key={tpl.id} className="surface-card p-5 flex flex-col" data-testid={`tpl-card-${tpl.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="h-10 w-10 rounded-md grid place-items-center bg-secondary/15 text-secondary">
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  {tpl.installed ? (
                    <Badge className="bg-success text-success-foreground"><CheckCircle2 className="h-3 w-3 mr-1" />Installed</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] font-mono">{tpl.feature_count} feats</Badge>
                  )}
                </div>
                <h3 className="font-display text-lg font-semibold mt-3">{tpl.name}</h3>
                <p className="text-xs text-primary mt-1 italic">{tpl.headline}</p>
                <p className="text-sm text-muted-foreground mt-3 flex-1">{tpl.description}</p>
                <Button
                  size="sm"
                  variant={tpl.installed ? "outline" : "default"}
                  className="mt-4 self-start"
                  disabled={tpl.installed || installingId === tpl.id}
                  onClick={() => onInstallTemplate(tpl)}
                  data-testid={`install-tpl-${tpl.id}`}
                >
                  {installingId === tpl.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
                  {tpl.installed ? "Already installed" : "Use template"}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Custom roles */}
      <div className="mt-8 surface-card overflow-hidden" data-testid="custom-roles-table">
        <div className="px-5 py-3 bg-muted/40 border-b border-border flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">Custom roles ({custom.length})</h3>
          <span className="text-xs text-muted-foreground">Editable · admin-managed</span>
        </div>
        {loading ? (
          <div className="p-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" /> Loading…</div>
        ) : custom.length === 0 ? (
          <div className="p-12 text-center">
            <KeyRound className="h-8 w-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-muted-foreground">No custom roles yet. Create your first one to start delegating admin tasks.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="p-3 pl-5">Name</th>
                <th className="p-3">Slug</th>
                <th className="p-3">Side</th>
                <th className="p-3">Parent</th>
                <th className="p-3">Users</th>
                <th className="p-3">Created</th>
                <th className="p-3 text-right pr-5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {custom.map((r) => (
                <tr key={r.slug} className="border-b border-border/50 last:border-0 hover:bg-accent/30" data-testid={`role-row-${r.slug}`}>
                  <td className="p-3 pl-5">
                    <div className="font-medium">{r.name}</div>
                    {r.description && <div className="text-[11px] text-muted-foreground mt-0.5">{r.description}</div>}
                  </td>
                  <td className="p-3 font-mono text-xs text-muted-foreground">{r.slug}</td>
                  <td className="p-3"><Badge variant="outline" className={r.side === "admin" ? "border-primary/40 text-primary" : "border-secondary/40 text-secondary"}>{r.side}</Badge></td>
                  <td className="p-3 text-xs">{r.parent || <em className="text-muted-foreground">empty</em>}</td>
                  <td className="p-3"><Badge variant={r.user_count > 0 ? "default" : "outline"}>{r.user_count}</Badge></td>
                  <td className="p-3 text-xs text-muted-foreground">{formatDate(r.created_at)}</td>
                  <td className="p-3 pr-5 text-right">
                    <Button size="sm" variant="ghost" onClick={() => onDelete(r.slug, r.name, r.user_count)}
                      disabled={r.user_count > 0 || deletingSlug === r.slug}
                      data-testid={`delete-role-${r.slug}`}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10">
                      {deletingSlug === r.slug ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Built-in roles */}
      <div className="mt-6 surface-card overflow-hidden" data-testid="builtin-roles-table">
        <div className="px-5 py-3 bg-muted/40 border-b border-border flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-success" />
          <h3 className="font-display text-lg font-semibold">Built-in roles ({builtin.length})</h3>
          <span className="text-xs text-muted-foreground ml-2">Read-only · cannot be deleted</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="p-3 pl-5">Role</th>
              <th className="p-3">Slug</th>
              <th className="p-3">Side</th>
              <th className="p-3">Users</th>
            </tr>
          </thead>
          <tbody>
            {builtin.map((r) => (
              <tr key={r.slug} className="border-b border-border/50 last:border-0">
                <td className="p-3 pl-5 font-medium">{r.label}</td>
                <td className="p-3 font-mono text-xs text-muted-foreground">{r.slug}</td>
                <td className="p-3"><Badge variant="outline">{r.side}</Badge></td>
                <td className="p-3"><Badge variant="outline" className="font-mono"><UsersIcon className="h-3 w-3 mr-1" /> {r.user_count}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create custom role</DialogTitle>
            <DialogDescription>Defines a new user type. After creation, edit the Feature matrix to grant or remove individual capabilities.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Role name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Finance Manager" data-testid="role-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Side</Label>
                <Select value={form.side} onValueChange={(v) => setForm({ ...form, side: v })}>
                  <SelectTrigger data-testid="role-side"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin console</SelectItem>
                    <SelectItem value="partner">Partner console</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">Determines which sidebar this role sees.</p>
              </div>
              <div>
                <Label>Inherit defaults from</Label>
                <Select value={form.parent} onValueChange={(v) => setForm({ ...form, parent: v })}>
                  <SelectTrigger data-testid="role-parent"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {parentChoices.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">Copies feature flags from this role.</p>
              </div>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What this role is responsible for…" data-testid="role-description" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={onCreate} disabled={busy} data-testid="create-role-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Create role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}
